import { forwardRef, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import {
  selectDoesUserHaveRbacPermission,
  selectIsSupportChatTestFeatureFlagOn,
} from 'core/userContext';
import { CircleSpinner } from 'ui/spinner';
import { BUTTON_VARIANTS, Button, ChatButton, IconButton } from 'ui/button';
import { Hamburger, LongArrowLeft, Overflow } from 'ui/icons';
import { ALIGNMENT, Menu } from 'ui/menu';
import { borderRadius, colors, shadows, spacing } from 'ui/theme';
import { Graph } from 'ui/workflows/flowchart';
import noop from 'utils/noop';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { CLONE_HAS_UNSAVED_CHANGES_MODAL, CLONE_JOURNEY_MODAL } from 'modules/workflows/constants';
import {
  getIsClonedFromJourney,
  getIsJourneyReviewPanelEnabled,
  getWorkflowHasUnsavedChanges,
  getWorkflowType,
  openModal,
  getIsWorkflowSaving,
} from 'modules/workflows/ducks';
import { CounterBadge } from 'ui/badge';
import styled from 'styled-components';
import { useJourneyAssistContext } from 'modules/workflows/context/JourneyAssistContext';
import { useJourneyReviewContext } from 'modules/workflows/context/JourneyReviewContext';
import { WorkflowType } from 'modules/workflows/enums';
import { Workflow } from '../../types';
import { JOURNEY_MENU_OPTION } from './constants';
import {
  HeaderBarContainer,
  HeaderBarWrapper,
  HeaderControlsLeft,
  HeaderControlsRight,
  HeaderIconButton,
  StyledQuickNav,
} from './HeaderBar.styles';
import { getButtonProps, checkLastURL } from './HeaderBar.utils';
import { HelpMenu } from './HelpMenu';
import { SearchBar } from './SearchBar';
import { WorkflowTitle } from './WorkflowTitle';

const StyledReviewButton = styled(Button)`
  display: flex;
  height: ${spacing.double};

  padding: ${spacing.half};
  align-items: center;
  gap: ${spacing.quarter};
  align-self: center;

  border-radius: ${borderRadius.full};
  border: 1px solid ${colors.borderPrimary};
  background: ${colors.backgroundPrimary};

  box-shadow: ${shadows.subtle};
`;

export const HEADER_BAR_WRAPPER_TEST_ID = 'header-bar-wrapper';
export const HEADER_BAR_TITLE_ID = 'header-bar-title';

export const ABOUT_FLOW_TEST_ID = 'journey-about-flow';
export const JOURNEY_MENU_TEST_ID = 'journey-manage-menu';
export const JOURNEY_REVIEW_BUTTON_TEST_ID = 'journey-review-button';

type HeaderBarProps = {
  /** id of the current workflow */
  workflowId: string;
  /** Callback when Navigation tips is clicked */
  onNavigationTips?: () => void;
  /** Flowchart graph uesd for searching */
  graph?: Graph | undefined;
  /** Is a node editor open? */
  nodeEditorOpened: boolean;
  workflow: Pick<
    Workflow,
    'id' | 'enabled' | 'name' | 'workflowType' | 'workflowReference' | 'projectId'
  >;
  /** Callback when "About" is clicked */
  onAboutFlowClick?: () => void;
  /** Callback when "Version history" clicked */
  setIsRevisionHistoryTakeoverOpen: (open: boolean) => void;
  handleReviewJourney: () => void;
};

const getJourneyMenuOptions = (hasDraftPermissions = false) => {
  const baseMenuOption = [JOURNEY_MENU_OPTION.SETTINGS, JOURNEY_MENU_OPTION.REVISION_HISTORY];

  if (!hasDraftPermissions) {
    return baseMenuOption;
  }

  return [...baseMenuOption, JOURNEY_MENU_OPTION.CLONE];
};

export const HeaderBar = forwardRef<HTMLDivElement, HeaderBarProps>(function HeaderBar(
  {
    nodeEditorOpened,
    workflow,
    onNavigationTips = noop,
    graph,
    onAboutFlowClick = noop,
    setIsRevisionHistoryTakeoverOpen = noop,
    handleReviewJourney = noop,
  }: HeaderBarProps,
  ref,
) {
  const dispatch = useDispatch();
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const triggerEl = useRef(null);
  const { isPublished, draftOnly } = useSelector(getWorkflowType);
  const history = useHistory();
  const buttonProps = getButtonProps(isPublished);
  const isWorkflowSaving = useSelector(getIsWorkflowSaving);

  // if the prev url is URL is from Studio or projectId does not match
  const lastURLIssue = checkLastURL(document.referrer, workflow?.projectId);
  const isClonedJourney = useSelector(getIsClonedFromJourney);
  const cloningRoutingIssues = isClonedJourney && draftOnly && lastURLIssue;
  // if history length is 1, cannot goBack (-1)
  const shouldRouteToJourneyIndex = cloningRoutingIssues || history.length <= 1;

  // code for ada/ zendesk support chat
  const isSupportChatTestEnabled = useSelector(selectIsSupportChatTestFeatureFlagOn);

  const isJourneyReviewPanelEnabled = useSelector(getIsJourneyReviewPanelEnabled);

  const canDraftMessaging = useSelector(selectDoesUserHaveRbacPermission)(
    PROJECT_PERMISSIONS.DraftMessaging,
  );

  const hasUnsavedChanges = useSelector(getWorkflowHasUnsavedChanges);

  const { isJourneyAssistActive } = useJourneyAssistContext();
  const {
    setIsOpen: setJourneyReviewOpen,
    incompleteIssuesCount,
    incompleteOptimizationsCount,
    isIssuesLoading,
    isOptimizationsLoading,
  } = useJourneyReviewContext();
  const totalCount = incompleteIssuesCount + incompleteOptimizationsCount;
  const isReviewPanelLoading = !isWorkflowSaving && (!!isIssuesLoading || !!isOptimizationsLoading);

  const handleSelectionChange = (selection: string) =>
    ({
      [JOURNEY_MENU_OPTION.SETTINGS.id]: () => onAboutFlowClick(),
      [JOURNEY_MENU_OPTION.CLONE.id]: () =>
        hasUnsavedChanges && !isPublished
          ? dispatch(openModal(CLONE_HAS_UNSAVED_CHANGES_MODAL, {}))
          : dispatch(openModal(CLONE_JOURNEY_MODAL, {})),
      [JOURNEY_MENU_OPTION.REVISION_HISTORY.id]: () => setIsRevisionHistoryTakeoverOpen(true),
    })[selection]();

  useEffect(() => {
    document.title = workflow.name
      ? `${workflow.name} | Journeys`
      : "Oops... we couldn't find that journey | Journeys";
    return () => {
      document.title = 'Journeys';
    };
  }, [workflow.name]);

  return (
    <HeaderBarWrapper data-test={HEADER_BAR_WRAPPER_TEST_ID} ref={ref}>
      <HeaderBarContainer data-test="header-bar" $isPublished={isPublished}>
        <HeaderControlsLeft data-test="header">
          <IconButton
            autoFocus={false}
            data-test="workflow-hamburger"
            ref={triggerEl}
            onClick={() => setIsNavOpen(!isNavOpen)}
            {...buttonProps}
          >
            <Hamburger isColorInherited />
          </IconButton>
          <HeaderIconButton>
            {!nodeEditorOpened && (
              <IconButton
                data-test="workflow-go-back"
                id="workflow-go-back"
                onClick={() =>
                  shouldRouteToJourneyIndex ? history.push('/workflows') : history.goBack()
                }
                {...buttonProps}
              >
                <LongArrowLeft color={colors.white} />
              </IconButton>
            )}
          </HeaderIconButton>
        </HeaderControlsLeft>
        <WorkflowTitle workflow={workflow} isPublished={isPublished} />
        <HeaderControlsRight>
          {!isSearchOpen && (
            <>
              {/* Flex metrics datepicker */}

              {isJourneyReviewPanelEnabled &&
                workflow.workflowType === WorkflowType.DRAFT &&
                !nodeEditorOpened && (
                  <StyledReviewButton
                    data-test={JOURNEY_REVIEW_BUTTON_TEST_ID}
                    onClick={() => {
                      if (hasUnsavedChanges) {
                        handleReviewJourney();
                      } else {
                        setJourneyReviewOpen(true);
                      }
                    }}
                    variant={BUTTON_VARIANTS.SECONDARY}
                    isDisabled={isJourneyAssistActive}
                  >
                    Review{' '}
                    {isReviewPanelLoading ? (
                      <CircleSpinner color={colors.textSecondary} size={14} />
                    ) : (
                      totalCount > 0 && <CounterBadge>{totalCount}</CounterBadge>
                    )}
                  </StyledReviewButton>
                )}
              {/* Studio menu options */}
              {!nodeEditorOpened && (
                <Menu
                  horizontalAlignment={ALIGNMENT.RIGHT}
                  verticalAlignment={ALIGNMENT.DOWN}
                  options={getJourneyMenuOptions(canDraftMessaging)}
                  id={JOURNEY_MENU_TEST_ID}
                  handleSelectionChange={handleSelectionChange}
                  data-test={JOURNEY_MENU_TEST_ID}
                >
                  <IconButton {...buttonProps}>
                    <Overflow height={18} width={18} isColorInherited />
                  </IconButton>
                </Menu>
              )}
              {/* Chat Support for studio header */}
              {isSupportChatTestEnabled && <ChatButton {...buttonProps} />}
              {/* Help menu for Documentation and Navigation Tips */}
              <HelpMenu onNavigationTips={onNavigationTips} {...buttonProps} />
            </>
          )}
          {/* Search */}
          <SearchBar
            graph={graph}
            isPublished={isPublished}
            onSearchOpen={setIsSearchOpen}
            buttonProps={buttonProps}
          />
        </HeaderControlsRight>
      </HeaderBarContainer>
      <StyledQuickNav
        isVisible={isNavOpen}
        triggerEl={triggerEl}
        toggleNav={() => setIsNavOpen(!isNavOpen)}
      />
    </HeaderBarWrapper>
  );
});
